/**
 * Scheduling-commitment client (constitution §3 + ratified decision #1).
 *
 * yanamas-assisted placement produces FLEXIBLE holds; the user confirms
 * whichever of them must not move. This module owns the verbs that move a
 * block between those two states, plus the create call that mints the
 * holds in the first place.
 *
 * Three rules every function here obeys:
 *
 *  - **Never confirm what the caller cannot name.** `POST
 *    /api/ai-scheduler/confirm-holds` rejects an unscoped body with 400 so
 *    one stray click can never mass-confirm every outstanding hold. This
 *    module goes further and offers only the `block_ids` scope (see
 *    ConfirmScope): a date span reaches rows the caller never displayed,
 *    which manufactures confirmations the user never gave.
 *  - **Address the row, not the item, whenever a row id exists.** The
 *    item-scoped fallback resolves an item to *whichever* live row it
 *    finds first, which is not necessarily the block on screen — see
 *    `setItemCommitment`.
 *  - **Never claim a mutation landed when it didn't.** A 202 with
 *    `applied:false` means "accepted, not yet applied"; so does a
 *    toggle-lock reply that persisted nothing. Both come back as
 *    `applied:false`, and callers must render those as pending, not done.
 */

// The §3 vocabulary itself lives in ./scheduleService — one definition of
// Flexible/Confirmed for the whole frontend. This module only moves blocks
// between those states; it does not get to name them.
import { updateScheduleEvent } from './scheduleService'
import type { CommitmentState, PlacementEdit } from './scheduleService'
import { BRAND } from '../config/brand'

/** Outcome of one mutation. `applied` is the honest bit. */
export interface VerbResult {
  /** The request was accepted (no transport or validation failure). */
  ok: boolean
  /** The change is live. False + ok = accepted but still pending. */
  applied: boolean
  /** Short, user-safe sentence. Never exposes internal vocabulary. */
  message?: string
  /** Set only when ok is false. */
  error?: string
}

/**
 * Scope for a confirm. Exactly one shape, and deliberately the exact one.
 *
 * The endpoint also accepts `date` and `date_range`, and this module does
 * not offer them. Those scopes confirm every proposed hold the server
 * finds in the span — including holds written by an earlier session that
 * the caller never rendered. Confirming a block nobody looked at is SC-6
 * pointed the other way: instead of nagging the user for a confirmation,
 * yanamas manufactures one. A surface that cannot name its rows must say
 * so and leave them Flexible; it must not widen the scope until they are
 * swept in. `partitionForConfirm` splits a batch along exactly that line.
 */
export type ConfirmScope = { block_ids: number[] }

export interface ConfirmHoldsResult extends VerbResult {
  confirmed_count: number
  /** Block ids the server actually stamped, for local reconciliation. */
  confirmed_ids: number[]
  /** Holds the day's shape could not take. Explain, never nag. */
  rejected: Array<{ id?: number; title?: string; date?: string; reason?: string }>
}

/** One item as the scheduler suggests / commits it. */
export interface SuggestedScheduleItem {
  item_id: number
  item_type: string
  title: string
  suggested_date: string
  suggested_time: string
  duration_minutes: number
  rationale?: string
  priority_score?: number
  grouped_with?: number[]
  is_recurring?: boolean
  recurrence?: unknown
}

export interface CreatedFlexibleBlock {
  /**
   * The schedule row's own id — the ONLY address the per-block verbs
   * accept (`confirmHolds({ block_ids })`, `removeBlockFromDay`). A hold
   * without one cannot be confirmed or released at all, because the only
   * other scope is a date span that reaches rows this session never
   * showed. Optional because a legacy server may still answer without it.
   */
  id?: number | null
  /**
   * True when `id` names a recurring SERIES row (a master) rather than one
   * dated block. Load-bearing for the verbs: `find_holds` matches the
   * master, so confirming this id confirms **every** occurrence the rule
   * produces, and `skip-today` releases exactly one of them while the rule
   * keeps generating the rest. A one-day-looking button must not commit a
   * rhythm, so callers label or withhold accordingly.
   */
  is_recurring_master?: boolean
  item_id: number
  item_type: string
  title: string
  date: string
  time_slot: string
  duration_minutes: number
  block_type: string
  block_status: string
}

export interface CreateFlexibleBlocksResult extends VerbResult {
  created: CreatedFlexibleBlock[]
  /** Items the day could not take — surfaced as explanation, not failure. */
  rejected: Array<{ item_id?: number; title?: string; reason?: string }>
  warnings: string[]
}

// ───────── addressing: which verb can reach which hold ─────────

/**
 * The bits of a hold that decide which verbs can reach it. Structural, so
 * both the planner's own card model and a raw read-model row satisfy it.
 */
export interface AddressableHold {
  /** The schedule row's own id, or null when this client has no address. */
  block_id?: number | null
  /** True when `block_id` names a recurring series row (a master). */
  is_recurring_master?: boolean
}

export interface ConfirmPartition<T> {
  /** One dated row each, named by id. Safe for a batch confirm. */
  addressable: T[]
  /**
   * Addressable, but the id names a RULE. Confirming one commits every day
   * the rule produces, so it is never swept into a batch — the user has to
   * ask for the rhythm by name.
   */
  series: T[]
  /** No row id at all. Nothing here may be confirmed by widening the scope. */
  unaddressable: T[]
}

/**
 * Split a batch of holds into what a "confirm these" action may honestly
 * send, what it must ask for one at a time, and what it has to leave alone.
 *
 * This is the whole of the C6 rule in one place: the only rows that go into
 * a `block_ids` scope are rows the caller can name, and there is no second
 * pass that widens the scope to cover the rest.
 */
export function partitionForConfirm<T extends AddressableHold>(
  holds: readonly T[],
): ConfirmPartition<T> {
  const partition: ConfirmPartition<T> = {
    addressable: [],
    series: [],
    unaddressable: [],
  }
  for (const hold of holds) {
    if (hold.block_id == null) partition.unaddressable.push(hold)
    else if (hold.is_recurring_master) partition.series.push(hold)
    else partition.addressable.push(hold)
  }
  return partition
}

/**
 * Whether a day-scoped release ENDS this hold's claim on the plan.
 *
 * False for a rule. `skip-today` on a recurring master materializes one
 * occurrence, skips it, and leaves the rule running — tomorrow's hold
 * appears as usual. A surface that removes the card on a successful
 * release is telling the user the rhythm is gone while yanamas is still
 * holding time for it, and it takes away the only control that could have
 * reached it (C12/C21).
 */
export function releaseEndsHold(hold: AddressableHold): boolean {
  return !hold.is_recurring_master
}

/**
 * Whether a verb's outcome may be reflected onto the card.
 *
 * The 202 `applied:false` contract in one predicate: accepted is not
 * applied, and the commitment chip is the only place the user can read
 * what yanamas is still allowed to move. Restyling it on `ok` alone
 * claims a stored state the server never reported (C9).
 */
export function mayRenderAsApplied(result: VerbResult): boolean {
  return result.ok && result.applied
}

/**
 * Item types the ITEM-scoped commitment flip can actually reach.
 *
 * `/api/ai-scheduler/toggle-lock` resolves an item to a schedule row with
 * `{parent_task_id: item_id}`. Only task-backed rows store the item's own
 * id there: an action item's row carries its id on `parent_action_item_id`
 * (and puts its *parent task's* id in `parent_task_id`), and habit / skill
 * / roadblock rows carry theirs on `source_item_id`. Sending any of those
 * ids is not a miss — it is a hit on whatever TASK happens to share the
 * number, so habit 7 mutates task 7's block. One list, so the guard and
 * the UI that withholds the verb cannot drift apart.
 */
export const ITEM_SCOPED_COMMITMENT_TYPES: readonly string[] = ['task']

export function canSetItemCommitment(itemType: string | undefined | null): boolean {
  return !!itemType && ITEM_SCOPED_COMMITMENT_TYPES.includes(itemType)
}

/** A schedule read-model row, as `GET /api/schedule/entries` returns it. */
export interface HoldRowLike {
  id?: number | string | null
  _id?: number | string | null
  is_virtual?: boolean
  source_item_id?: number | string | null
  source_item_type?: string | null
  recurring_source_id?: number | string | null
  recurring_source_type?: string | null
  parent_action_item_id?: number | string | null
  parent_task_id?: number | string | null
  task_kind?: string | null
}

/** Which item a hold row belongs to, as a typed pair. */
export interface HoldSource {
  itemId: string
  itemType: string
}

const present = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== ''

/**
 * Resolve the ITEM a hold row was written for.
 *
 * The ladder mirrors the server's own (`scheduling_policy.source_item_of`):
 * `source_item_*` → `recurring_source_*` → `parent_action_item_id` →
 * `parent_task_id`. Keying on `parent_task_id` alone — which is what the
 * planner's reload used to do — silently discards every habit, skill and
 * roadblock hold, because those rows leave it null and carry their origin
 * on `source_item_id`. A discarded hold has no card and therefore no verb:
 * yanamas holds the time and the user cannot see it, move it or release it.
 *
 * Returns null for a row with no resolvable owner. Those are orphans, and
 * inventing `task:undefined` for them would collide every one of them onto
 * a single card.
 */
export function holdSourceOf(row: HoldRowLike): HoldSource | null {
  if (present(row.source_item_id) && present(row.source_item_type)) {
    return { itemId: String(row.source_item_id), itemType: String(row.source_item_type) }
  }
  if (present(row.recurring_source_id)) {
    const type = present(row.recurring_source_type)
      ? String(row.recurring_source_type)
      : row.task_kind === 'habit_occurrence'
        ? 'habit'
        : null
    if (type) return { itemId: String(row.recurring_source_id), itemType: type }
  }
  if (present(row.parent_action_item_id)) {
    return { itemId: String(row.parent_action_item_id), itemType: 'action_item' }
  }
  if (present(row.parent_task_id)) {
    return { itemId: String(row.parent_task_id), itemType: 'task' }
  }
  return null
}

/**
 * The row's own id, when it has one this client can address.
 *
 * A virtual occurrence expanded from a recurring master carries the
 * synthetic `"<master>_v_<date>"` id and is deliberately left
 * unaddressable rather than resolved back to its master: skipping that day
 * through the master would be right, but CONFIRMING the master confirms
 * the whole series, and no per-day control may do that behind the user's
 * back.
 */
export function holdRowIdOf(row: HoldRowLike): number | null {
  if (row.is_virtual) return null
  const raw = row.id ?? row._id
  if (raw == null || raw === '') return null
  const parsed = Number(raw)
  return Number.isFinite(parsed) ? parsed : null
}

interface RawResponse {
  status: number
  body: Record<string, unknown>
}

async function postJson(url: string, body: unknown): Promise<RawResponse> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  })
  let parsed: Record<string, unknown> = {}
  try {
    parsed = (await res.json()) as Record<string, unknown>
  } catch {
    /* empty or non-JSON body — treated as no payload */
  }
  return { status: res.status, body: parsed }
}

/**
 * Collapse one HTTP reply into the ok/applied pair.
 *
 * 202 + `applied:false` is the Auto-Mom "accepted, not yet applied"
 * contract: the request is safely queued but the schedule has NOT
 * changed yet, so it must never be rendered as done.
 */
function readVerbResult(raw: RawResponse, pendingMessage: string): VerbResult {
  const { status, body } = raw
  const succeeded = body.success !== false
  if (status === 202) {
    const applied = body.applied === true
    return {
      ok: true,
      applied,
      message: applied ? undefined : pendingMessage,
    }
  }
  if (status < 200 || status >= 300 || !succeeded) {
    return {
      ok: false,
      applied: false,
      error:
        typeof body.error === 'string' && body.error
          ? body.error
          : `Request failed (${status})`,
    }
  }
  return { ok: true, applied: true }
}

const PENDING_COPY = `Saved — ${BRAND.assistant} is still writing this to your calendar.`

/**
 * The register for "the verb could not get to that block". Honest and
 * final: nothing was written, nothing is queued, and there is nothing for
 * the user to do about it (SC-6 — no chore is created by a limitation of
 * ours). Shared with the Clear path, which says the same thing about a
 * hold it could not release.
 */
const UNREACHABLE_COPY = 'Could not reach that one from here — nothing changed.'

/**
 * Ask the scheduler to place these items as FLEXIBLE holds
 * (`ai_schedule_mode: 'hold'`).
 *
 * Ratified decision #1: yanamas-assisted placement is HOLD-first. The
 * server stamps every row it writes here `flexible` / `ai_proposal` and
 * validates against the (more generous) daily hold limit rather than the
 * focus limit — a plan yanamas owns may be wider than the day the user
 * has committed to.
 */
export async function createFlexibleBlocks(
  items: SuggestedScheduleItem[],
): Promise<CreateFlexibleBlocksResult> {
  const empty = { created: [], rejected: [], warnings: [] }
  if (items.length === 0) {
    return { ok: true, applied: true, ...empty }
  }
  try {
    const raw = await postJson('/api/ai-scheduler/create-blocks', {
      scheduled_items: items,
      ai_schedule_mode: 'hold',
    })
    const base = readVerbResult(raw, PENDING_COPY)
    return {
      ...base,
      // Normalise the row id to `number | null` at the seam so no caller
      // has to guess whether a hold is addressable.
      created: ((raw.body.created_blocks as CreatedFlexibleBlock[]) || []).map(
        (block) => ({
          ...block,
          id:
            typeof block?.id === 'number' && Number.isFinite(block.id)
              ? block.id
              : null,
          // Normalised here too, for the same reason as `id`: whether this
          // address names one day or a whole rhythm decides which verbs the
          // caller may offer, and a caller must not have to reason about
          // `undefined` vs `false` to find that out.
          is_recurring_master: block?.is_recurring_master === true,
        }),
      ),
      rejected:
        (raw.body.rejected_items as CreateFlexibleBlocksResult['rejected']) || [],
      warnings: (raw.body.warnings as string[]) || [],
    }
  } catch (e) {
    console.error('[scheduler] create-blocks failed:', e)
    return {
      ok: false,
      applied: false,
      error: 'Could not reach the scheduler.',
      ...empty,
    }
  }
}

/**
 * Confirm exactly the rows named in `scope.block_ids`, and nothing else.
 *
 * The scope is mandatory by construction (see ConfirmScope): an unscoped
 * body is refused server-side with 400, and no scope this module can
 * express reaches a row the caller did not name.
 *
 * One id caveat the caller owns: `find_holds` matches a recurring MASTER
 * by id, so passing a master's id confirms the entire series. Split a
 * batch with `partitionForConfirm` before calling.
 */
export async function confirmHolds(
  scope: ConfirmScope,
): Promise<ConfirmHoldsResult> {
  const empty = { confirmed_count: 0, confirmed_ids: [], rejected: [] }
  try {
    const raw = await postJson('/api/ai-scheduler/confirm-holds', scope)
    const base = readVerbResult(raw, PENDING_COPY)
    const confirmed =
      (raw.body.confirmed_blocks as Array<{ id?: number }>) || []
    return {
      ...base,
      confirmed_count: (raw.body.confirmed_count as number) ?? confirmed.length,
      confirmed_ids: confirmed
        .map((b) => b?.id)
        .filter((id): id is number => typeof id === 'number'),
      rejected:
        (raw.body.rejected_blocks as ConfirmHoldsResult['rejected']) || [],
    }
  } catch (e) {
    console.error('[scheduler] confirm-holds failed:', e)
    return {
      ok: false,
      applied: false,
      error: 'Could not reach the scheduler.',
      ...empty,
    }
  }
}

/**
 * Flip one ITEM's earliest live block between Flexible and Confirmed.
 *
 * The imprecise address, and the last resort. The server resolves the item
 * to whichever live row sorts first (`parent_task_id` match, today or
 * later, earliest date/time) — which is emphatically not "the block the
 * user is looking at". Two guards keep that from becoming a silent write
 * to the wrong row:
 *
 *  - `itemType` must be one the query can actually reach
 *    (`ITEM_SCOPED_COMMITMENT_TYPES`). A habit id sent here does not miss;
 *    it lands on the task that happens to share the number.
 *  - `expectedBlockId`, when the caller knows which row it meant, is
 *    checked against the row the server reports back. A mismatch is
 *    reported as not-applied so the card is never restyled to describe a
 *    different day's block.
 *
 * Prefer a row-scoped verb (`confirmHolds({ block_ids })`) whenever a row
 * id exists; this exists for the holds that have none.
 */
export async function setItemCommitment(
  itemId: number,
  state: CommitmentState,
  itemType: string,
  expectedBlockId?: number | null,
): Promise<VerbResult> {
  // Refused before the request, not after: the point is that the write
  // must not happen, and a 200 from a query that matched the wrong row
  // looks exactly like success.
  if (!canSetItemCommitment(itemType)) {
    return { ok: true, applied: false, message: UNREACHABLE_COPY }
  }
  try {
    const raw = await postJson('/api/ai-scheduler/toggle-lock', {
      item_id: itemId,
      new_status: state === 'confirmed' ? 'confirmed' : 'proposed',
    })
    const base = readVerbResult(raw, PENDING_COPY)
    if (!base.ok || !base.applied) return base
    // The route answers 200 with no block_id when the item has no
    // schedule row it can address ("in-memory only"). Nothing was
    // written, so this is pending, not done.
    const touched = raw.body.block_id
    if (touched == null) {
      return {
        ok: true,
        applied: false,
        message:
          state === 'confirmed'
            ? 'Nothing on the calendar to confirm for this one yet.'
            : 'Still flexible — nothing to change.',
      }
    }
    if (expectedBlockId != null && Number(touched) !== Number(expectedBlockId)) {
      return {
        ok: true,
        applied: false,
        message: 'That reached a different time for this one — reloading your plan.',
      }
    }
    return base
  } catch (e) {
    console.error('[scheduler] toggle-lock failed:', e)
    return { ok: false, applied: false, error: 'Could not reach the scheduler.' }
  }
}

/**
 * Take one block off ONE day (SC-16 `skipped_by_user`), leaving the parent
 * item, the series, and every other occurrence untouched.
 *
 * Read that last clause literally when `blockId` is a recurring master:
 * the server materializes that one occurrence as an exception and skips
 * it, and the rule keeps producing every other day. This is "Not today",
 * never "stop doing this" — a caller that removes the card afterwards is
 * telling the user the rhythm is gone when yanamas is still holding time
 * for it tomorrow.
 *
 * Every 2xx result means the block is off the day — `already_skipped`
 * and `already_terminal` included. Neither is a failure and neither
 * earns a follow-up prompt (SC-6).
 */
export async function removeBlockFromDay(
  blockId: number,
  date: string,
): Promise<VerbResult> {
  try {
    const raw = await postJson(
      `/api/schedule/events/${blockId}/skip-today`,
      { date },
    )
    if (raw.status >= 200 && raw.status < 300) {
      return { ok: true, applied: true }
    }
    return readVerbResult(raw, PENDING_COPY)
  } catch (e) {
    console.error('[scheduler] skip-today failed:', e)
    return { ok: false, applied: false, error: 'Could not reach the scheduler.' }
  }
}

/**
 * Re-place one stored block. Requires the block's own row id.
 *
 * Delegates to the shared placement writer (`updateScheduleEvent`), which
 * already carries the SC-7 secondary: a user-chosen time confirms the
 * block unless `keepFlexible` says otherwise. This wrapper only adapts its
 * throw-on-failure shape to the VerbResult the page renders.
 */
export async function moveBlock(
  blockId: number,
  edit: PlacementEdit,
): Promise<VerbResult> {
  try {
    const outcome = await updateScheduleEvent(blockId, edit)
    return {
      ok: true,
      applied: outcome.applied,
      message: outcome.applied ? undefined : PENDING_COPY,
    }
  } catch (e) {
    console.error('[scheduler] move block failed:', e)
    return {
      ok: false,
      applied: false,
      error: e instanceof Error ? e.message : 'Could not reach the scheduler.',
    }
  }
}
